import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

// Zeigt die beiden hyper-mesh-Szenen (Simulation, keine Messdaten) zum Umschalten:
//  1 = Stadtteil nur mit Batteriespeichern, 2 = Bahn-Unterwerk mit Schwungmasse.
// Auf Handys / bei Datensparmodus / langsamer Verbindung wird die kleine Videodatei geladen.

const SCENES = {
  1: { full: '/videos/grid-battery.mp4', small: '/videos/grid-battery-small.mp4' },
  2: { full: '/videos/grid-train.mp4', small: '/videos/grid-train-small.mp4' },
};

export function useSmallVideo() {
  const [small, setSmall] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => {
      const conn = navigator.connection;
      setSmall(mq.matches || Boolean(conn && (conn.saveData || /(^|-)(2g|3g)$/.test(conn.effectiveType || ''))));
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return small;
}

export default function GridScenarioVideos({ onClose, initialScene = 1 }) {
  const { t } = useTranslation();
  const [scene, setScene] = useState(initialScene);
  const small = useSmallVideo();
  const rootRef = useRef(null);

  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const src = small ? SCENES[scene].small : SCENES[scene].full;
  const tab = (n, label) => (
    <button
      type="button"
      onClick={() => setScene(n)}
      aria-pressed={scene === n}
      data-testid={`hyper-grid-scene-${n}`}
      className={`flex-1 px-3 py-2 text-sm font-semibold rounded-full transition-colors ${
        scene === n ? 'bg-gradient-to-r from-green-500 to-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      ref={rootRef}
      className="max-w-4xl mx-auto mb-12 p-4 sm:p-6 rounded-2xl border border-slate-700 bg-slate-800/60 text-left"
      data-testid="hyper-grid-scenario"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="text-lg sm:text-xl font-bold">{t('hyperGrid.scenarioTitle')}</h2>
        <button type="button" onClick={onClose} aria-label={t('hyperGrid.scenarioClose')} className="text-slate-400 hover:text-white">
          <X size={22} />
        </button>
      </div>
      <div className="flex gap-2 mb-4">
        {tab(1, t('hyperGrid.scene1Tab'))}
        {tab(2, t('hyperGrid.scene2Tab'))}
      </div>
      <video
        key={src}
        src={src}
        className="w-full rounded-xl bg-black"
        controls
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        data-testid="hyper-grid-video"
      />
      <p className="text-slate-300 mt-4 leading-relaxed">{t(`hyperGrid.scene${scene}Text`)}</p>
      <p className="text-xs text-slate-500 mt-2">{t('hyperGrid.videoNote')}</p>
    </div>
  );
}
